// Signup.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { userSignup } from '../../services/signup';
import { validatePassword, getPasswordRequirementStatus } from '../../utils/passwordValidation';
import LoadingSpinner from '../loadingSpinner/LoadingSpinner';
import styles from './signup.module.css';

export default function Signup() {
  const [firstNameField, setFirstNameField] = useState('');
  const [lastNameField, setLastNameField] = useState('');
  const [displayNameField, setDisplayNameField] = useState('');
  const [passwordField, setPasswordField] = useState('');
  const [repeatPwField, setRepeatPwField] = useState('');
  // const [emailField] = useState('Automatically set Email');
  // const [orgField] = useState('Automatically set Organization');
  // const [accessExpField] = useState('Automatically set access expiration');
  const [submitDisable, setSubmitDisable] = useState(true);
  const [message, setMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [requirementStatus, setRequirementStatus] = useState({
    minLength: false,
    noConsecutive: false,
    noInvalidChars: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'KNOW - Signup';
  }, []);

  useEffect(() => {
    if (passwordField === '') {
      setPasswordError('');
      setSubmitDisable(true);
      setRequirementStatus({ minLength: false, noConsecutive: false, noInvalidChars: true });
      return;
    }

    const debounceTimer = setTimeout(() => {
      setRequirementStatus(getPasswordRequirementStatus(passwordField));

      const validation = validatePassword(passwordField);
      if (!validation.valid) {
        setPasswordError('');
        setSubmitDisable(true);
        return;
      }

      if (passwordField !== repeatPwField) {
        setPasswordError(repeatPwField ? 'Passwords do not match.' : '');
        setSubmitDisable(true);
        return;
      }

      setPasswordError('');
      setSubmitDisable(false);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [passwordField, repeatPwField]);

  if (submitLoading) return <LoadingSpinner />;

  const messageWithClear = (text, timeout = 3000) => {
    setMessage(text);
    setTimeout(() => setMessage(''), timeout);
  };

  const handleFirstNameChange = ({ target }) => setFirstNameField(target.value);
  const handleLastNameChange = ({ target }) => setLastNameField(target.value);
  const handleDisplayNameFieldChange = ({ target }) => setDisplayNameField(target.value);
  const handlePasswordFieldChange = ({ target }) => setPasswordField(target.value);
  const handleRepeatPwFieldChange = ({ target }) => setRepeatPwField(target.value);
  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmitLoading(true);
    const token = new URL(document.location).searchParams.get('token');
    const userInfo = {
      first_name: firstNameField,
      last_name: lastNameField,
      display_name: displayNameField,
      password: passwordField,
      token,
    };
    userSignup(userInfo)
      .then(({ data: newUser }) => {
        messageWithClear(
          `${newUser.message} You will be redirected to the login page in a few moments.`,
          3500
        );
        setSubmitLoading(false);
        setTimeout(() => navigate('/login', { replace: true }), 3600);
      })
      .catch((err) => {
        messageWithClear(err.message, 10000);
        setSubmitLoading(false);
      });
  };

  return (
    <>
      <div className={styles.signupContainer} role="main" aria-label="sign up form">
        <h1>Signup</h1>
        <p className={styles.message}>{message}</p>
        <div className={styles.formContainer}>
          <form>
            <label htmlFor="firstName">First Name</label>
            <input
              id="firstName"
              type="text"
              value={firstNameField}
              onChange={handleFirstNameChange}
              placeholder="First name"
            />
            <label htmlFor="lastName">Last Name</label>
            <input
              id="lastName"
              type="text"
              value={lastNameField}
              onChange={handleLastNameChange}
              placeholder="Last name"
            />
            <label htmlFor="displayName">Display Name</label>
            <input
              id="displayName"
              type="text"
              value={displayNameField}
              onChange={handleDisplayNameFieldChange}
              placeholder="Display name"
            />
            <label htmlFor="password1">Password</label>
            <div className={styles.passwordInputWrapper}>
              <input
                id="password1"
                type={showPassword ? 'text' : 'password'}
                value={passwordField}
                onChange={handlePasswordFieldChange}
                placeholder="Password"
              />
              <button
                type="button"
                tabIndex={-1}
                className={styles.togglePassword}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FaEye /> : <FaEyeSlash />}
              </button>
            </div>
            <div className={styles.passwordRequirements}>
              <strong>Password requirements:</strong>
              <ul>
                <li
                  className={
                    requirementStatus.minLength ? styles.requirementMet : styles.requirementNotMet
                  }
                >
                  {requirementStatus.minLength ? '✓' : '○'} At least 12 characters
                </li>
                <li
                  className={
                    requirementStatus.noConsecutive
                      ? styles.requirementMet
                      : styles.requirementNotMet
                  }
                >
                  {requirementStatus.noConsecutive ? '✓' : '○'} No more than 3 consecutive identical
                  characters
                </li>
              </ul>
            </div>
            <label htmlFor="password2">Repeat Password</label>
            <div className={styles.passwordInputWrapper}>
              <input
                id="password2"
                type={showRepeatPassword ? 'text' : 'password'}
                value={repeatPwField}
                onChange={handleRepeatPwFieldChange}
                placeholder="Repeat password"
              />
              <button
                type="button"
                tabIndex={-1}
                className={styles.togglePassword}
                onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                aria-label={showRepeatPassword ? 'Hide password' : 'Show password'}
              >
                {showRepeatPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {passwordError && <p className={styles.error}>{passwordError}</p>}
            <button disabled={submitDisable} type="submit" onClick={handleSubmit}>
              Sign Up
            </button>
          </form>
        </div>
        <p>{message}</p>
      </div>
    </>
  );
}
